import React, {useRef, useState} from 'react';
import {styled} from '@mui/system';
import ContainerContent from '../../common/container/ContainerContent';
import HorizontalSeparator from "../../common/separator/HorizontalSeparator";
import {SubmissionTranslKey} from "../submission/SubmissionTranslKey";
import {useTranslation} from "react-i18next";
import MainMenuSiteName from "./MainMenuSiteName";
import MainMenuLionIcon from "./MainMenuLionIcon";
import MainMenuPictogram from "./MainMenuPictogram";
import MainMenuLink from "./MainMenuLink";
import NavItemLink from '../../common/nav/NavItemLink';
import NavItemIcon from "../../common/nav/NavItemIcon";
import {HouseDoorFill} from "../../common/icon/Icon";
import {
    ADMIN_PERSON_OVERVIEW,
    ADMIN_SUBMISSIONS,
    ADMIN_USER_OVERVIEW,
    DASHBOARD,
    FORM_SESSIONS,
    ISDS_MESSAGE_OVERVIEW,
    ISDS_MESSAGE_STATE_OVERVIEW,
    SUBMISSIONS
} from "../../AppRoutes";
import {FormSessionTranslKey} from "../session/FormSessionTranslKey";
import Nav from "../../common/nav/Nav";
import {useUserRoles} from "../user/UserContext";
import UserRoleResolver from "../user/UserRoleResolver";
import {ListItemIcon, ListItemText, Menu, MenuItem} from "@mui/material";
import {
    AccountBox,
    ArchiveOutlined,
    Person,
    MailOutline,
    AssessmentOutlined
} from "@mui/icons-material";
import {useNavigate} from "react-router-dom";
import {PersonTranslKey} from "../person/PersonTranslKey";
import {EnvConfiguration, PortalProfile} from "../../EnvConfig";
import {UserTranslKey} from "../user/UserTranslKey";
import {CommonTranslKey} from "../../common/CommonTranslKey";
import NavItem from "../../common/nav/NavItem";
import {MessageTranslKey} from "../message/MessageTranslKey";
import {MessageStateTranslKey} from "../message-state/MessageStateTranslKey";

const StyledHeader = styled('header')({
    overflow: 'initial',
    backgroundColor: 'var(--gov-color-blue)',
    padding: '0 1.25rem',
});

const StyledHeaderHolder = styled('div')({
    paddingTop: '1.25rem',
    paddingBottom: '1.0625rem',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
});

const StyledHeaderRight = styled('div')(() => ({
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
}));

const StyledNavWrapper = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
});

const StyledLeftSection = styled('div')({
    display: 'flex',
    alignItems: 'stretch',
    whiteSpace: 'nowrap',
});

const StyledRightSection = styled('div')({
    display: 'flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
});

const StyledLeftItems = styled('div')({
    display: 'flex',
    alignItems: 'stretch',
    '& > *:not(:last-child)': {
        marginRight: '1rem',
    },
});

const StyledHeaderLeft = styled('div')({});

interface HeaderProps {
    children?: React.ReactNode;
}

export default function MainHeader(props: HeaderProps) {
    const {children} = props;

    const [adminOverviewElement, setAdminOverviewElement] = useState<Element | null>(
        null
    );

    const navigate = useNavigate();

    const userMenuOpen = Boolean(adminOverviewElement);

    function onPersonOverviewClick() {
        navigate(ADMIN_PERSON_OVERVIEW);
        setAdminOverviewElement(null);
    }

    function onUserOverviewClick() {
        navigate(ADMIN_USER_OVERVIEW);
        setAdminOverviewElement(null);
    }

    function onIsdsMessageOverviewClick() {
        navigate(ISDS_MESSAGE_OVERVIEW);
        setAdminOverviewElement(null);
    }

    function onIsdsMessageStateOverviewClick() {
        navigate(ISDS_MESSAGE_STATE_OVERVIEW);
        setAdminOverviewElement(null);
    }

    function onSubmissionClick() {
        navigate(ADMIN_SUBMISSIONS);
        setAdminOverviewElement(null);
    }

    const {t} = useTranslation();
    const roles = useUserRoles();
    const isAdministrator = UserRoleResolver.isAdministrator(roles);
    const isAtLeastOperator = UserRoleResolver.isAtLeastOperator(roles);

    const envConfig = useRef(EnvConfiguration.getConfig());

    function getMainTitle(profile: string): string {
        if (profile === PortalProfile.MV) {
            return t(SubmissionTranslKey.SUBMISSION_PORTAL_MV);
        } else if (profile === PortalProfile.MO) {
            return t(SubmissionTranslKey.SUBMISSION_PORTAL_MO);
        } else {
            return t(SubmissionTranslKey.SUBMISSION_PORTAL_DEFAULT);
        }
    }

    return (
        <StyledHeader>
            <ContainerContent>
                <StyledHeaderHolder>
                    <StyledHeaderLeft>
                        <MainMenuLink href="/">
                            <MainMenuPictogram>
                                <MainMenuLionIcon />
                            </MainMenuPictogram>
                            <MainMenuSiteName>
                                {getMainTitle(envConfig.current.profile)}
                            </MainMenuSiteName>
                        </MainMenuLink>
                    </StyledHeaderLeft>
                    <StyledHeaderRight>
                        {children}
                    </StyledHeaderRight>
                </StyledHeaderHolder>

                <HorizontalSeparator />

                <Nav>
                    <StyledNavWrapper>
                        <StyledLeftSection>
                            <StyledLeftItems>
                                <NavItemLink to={DASHBOARD}>
                                    <NavItemIcon icon={HouseDoorFill} />
                                </NavItemLink>
                                <NavItemLink to={SUBMISSIONS} hidden={!isAdministrator}>
                                    {t(SubmissionTranslKey.RECEIVED_DOCUMENTS)}
                                </NavItemLink>
                                <NavItemLink to={FORM_SESSIONS} hidden={!isAdministrator}>
                                    {t(FormSessionTranslKey.FORM_SESSIONS_OVERVIEW)}
                                </NavItemLink>
                            </StyledLeftItems>
                        </StyledLeftSection>
                        <StyledRightSection>
                            {isAtLeastOperator && (
                                <>
                                    <NavItem onClick={(e) => setAdminOverviewElement(e.currentTarget)}>
                                        {t(CommonTranslKey.ADMINISTRATION)}
                                    </NavItem>
                                    <Menu
                                        anchorEl={adminOverviewElement}
                                        anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
                                        transformOrigin={{horizontal: 'right', vertical: 'top'}}
                                        open={userMenuOpen}
                                        onClose={() => setAdminOverviewElement(null)}
                                    >
                                        {isAdministrator && (
                                            <>
                                                <MenuItem onClick={onPersonOverviewClick}>
                                                    <ListItemIcon>
                                                        <Person />
                                                    </ListItemIcon>
                                                    <ListItemText>{t(PersonTranslKey.PERSON_OVERVIEW)}</ListItemText>
                                                </MenuItem>
                                                <MenuItem onClick={onUserOverviewClick}>
                                                    <ListItemIcon>
                                                        <AccountBox />
                                                    </ListItemIcon>
                                                    <ListItemText>{t(UserTranslKey.USER_OVERVIEW)}</ListItemText>
                                                </MenuItem>
                                                <MenuItem onClick={onIsdsMessageOverviewClick}>
                                                    <ListItemIcon>
                                                        <MailOutline />
                                                    </ListItemIcon>
                                                    <ListItemText>{t(MessageTranslKey.ISDS_MESSAGE_OVERVIEW)}</ListItemText>
                                                </MenuItem>
                                                <MenuItem onClick={onIsdsMessageStateOverviewClick}>
                                                    <ListItemIcon>
                                                        <AssessmentOutlined />
                                                    </ListItemIcon>
                                                    <ListItemText>{t(MessageStateTranslKey.ISDS_STATE_OVERVIEW)}</ListItemText>
                                                </MenuItem>
                                            </>

                                        )}
                                        <MenuItem onClick={onSubmissionClick}>
                                            <ListItemIcon>
                                                <ArchiveOutlined />
                                            </ListItemIcon>
                                            <ListItemText>{t(SubmissionTranslKey.DOCUMENTS_MANAGEMENT)}</ListItemText>
                                        </MenuItem>
                                    </Menu>
                                </>
                            )}
                        </StyledRightSection>
                    </StyledNavWrapper>
                </Nav>
            </ContainerContent>
        </StyledHeader>
    );
}
